// src/components/UserModal.jsx
import React, { useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Badge } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import { FunctionDictionary } from '../config/functionDictionary';

// Options pour le multi-select
const RIGHT_OPTIONS = Object.values(FunctionDictionary).map(({ code, label }) => ({
  value: code,
  label: `${code} — ${label}`,
}));

// --- Helpers de normalisation ---
const toArray = (v) => {
  if (Array.isArray(v)) return v;
  if (v == null) return [];
  if (typeof v === 'string') {
    // essaye JSON, sinon split par virgule
    try {
      const parsed = JSON.parse(v);
      if (Array.isArray(parsed)) return parsed;
    } catch (_) { /* ignore */ }
    return v.split(',').map(s => s.trim()).filter(Boolean);
  }
  if (typeof v === 'object') {
    // ex: {0:"USER_VIEW",1:"CLIENT_VIEW"} → ["USER_VIEW","CLIENT_VIEW"]
    return Object.values(v);
  }
  return [];
};

const toSelectValues = (rights) =>
  toArray(rights)
    .map(code => RIGHT_OPTIONS.find(o => o.value === code))
    .filter(Boolean);

export default function UserModal({ show, onHide, onSave, initialData = {} }) {
  const isEdit = !!initialData?.id;

  const {
    register, handleSubmit, reset, control, watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      username: initialData?.username || '',
      email:    initialData?.email    || '',
      password: '',
      rights:   toSelectValues(initialData?.rights),
    }
  });

  // Réinitialise quand on ouvre / change d'utilisateur
  useEffect(() => {
    if (!show) return;
    reset({
      username: initialData?.username || '',
      email:    initialData?.email    || '',
      password: '',
      rights:   toSelectValues(initialData?.rights),
    });
  }, [show, initialData, reset]);

  const submit = (vals) => {
    // sécuriser vals.rights (peut être array d’objets {value,label} ou array de strings)
    const sel = Array.isArray(vals.rights) ? vals.rights : toArray(vals.rights);
    const rights = sel.map(r => (typeof r === 'string' ? r : r?.value)).filter(Boolean);

    const payload = {
      username: vals.username.trim(),
      email: vals.email?.trim() || null,
      rights,
    };
    if (!isEdit) {
      payload.password = vals.password; // requis en création
    } else if (vals.password && vals.password.trim()) {
      payload.password = vals.password.trim(); // changement volontaire
    }

    onSave(payload);
  };

  const selectedRights = Array.isArray(watch('rights')) ? watch('rights') : [];

  return (
    <Modal show={show} onHide={onHide} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Modifier un utilisateur' : 'Créer un utilisateur'}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit(submit)}>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="username">
                <Form.Label>Nom d’utilisateur *</Form.Label>
                <Form.Control
                  {...register('username', { required: 'Requis', minLength: { value: 3, message: '≥ 3 caractères' } })}
                />
                {errors.username && <Form.Text className="text-danger">{errors.username.message}</Form.Text>}
              </Form.Group>
            </Col>

            {/* Email (optionnel)
            <Col md={6}>
              <Form.Group controlId="email">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  {...register('email', { pattern: { value: /^\S+@\S+\.\S+$/, message: 'Email invalide' } })}
                />
                {errors.email && <Form.Text className="text-danger">{errors.email.message}</Form.Text>}
              </Form.Group>
            </Col>
            */}
          </Row>

          <Form.Group className="mt-3" controlId="password">
            <Form.Label>Mot de passe {isEdit ? '(laisser vide pour ne pas changer)' : '*'}</Form.Label>
            <Form.Control
              type="password"
              {...register('password', isEdit ? {} : { required: 'Requis', minLength: { value: 6, message: '≥ 6 caractères' } })}
            />
            {errors.password && <Form.Text className="text-danger">{errors.password.message}</Form.Text>}
          </Form.Group>

          <Form.Group className="mt-3" controlId="rights">
            <Form.Label>Droits</Form.Label>
            <Controller
              control={control}
              name="rights"
              render={({ field }) => (
                <Select
                  {...field}
                  isMulti
                  options={RIGHT_OPTIONS}
                  placeholder="Sélectionner 1+ droits…"
                />
              )}
            />
            <div className="mt-2">
              {selectedRights.map((r) => (
                <Badge key={r.value} bg="secondary" className="me-1">{r.value}</Badge>
              ))}
            </div>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Annuler</Button>
          <Button type="submit" variant="primary">{isEdit ? 'Enregistrer' : 'Créer'}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
